import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { 
  ShieldCheck, 
  HeartHandshake, 
  Clock, 
  ArrowRight, 
  CheckCircle2, 
  Phone,
  Menu,
  X,
  ChevronDown,
  Users,
  Award,
  Zap,
  Star,
  Dumbbell,
  Heart,
  Gift,
  CreditCard,
  Sparkles
} from "lucide-react";
import { Toaster, toast } from "sonner";

const RAW_BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/$/, "");
const API = RAW_BACKEND_URL
  ? (RAW_BACKEND_URL.endsWith("/api") ? RAW_BACKEND_URL : `${RAW_BACKEND_URL}/api`)
  : "http://localhost:8001/api";
const INITIAL_FORM_DATA = {
  firstName: "",
  lastName: "",
  email: "",
  isTrial: false
};
const PAYPAL_CONFIG_TIMEOUT_MS = 12000;
const PAYMENT_REQUEST_TIMEOUT_MS = 20000;
const PAYPAL_V5_SCRIPT_ID = "paypal-js-sdk-v5";

const getApiErrorMessage = (error, fallbackMessage) => {
  return error?.response?.data?.detail || fallbackMessage;
};

const getPayPalErrorMessage = (error) => {
  const detailsMessage = error?.details?.[0]?.description;
  const baseMessage = detailsMessage || error?.message || "Unable to complete checkout. Please try again.";
  const debugId = error?.details?.[0]?.debug_id || error?.debug_id;
  return debugId ? `${baseMessage} (Ref: ${debugId})` : baseMessage;
};

const getPayPalV5ScriptSrc = (mode = "live", clientId = "", clientToken = "") => {
  const encodedClientId = encodeURIComponent(clientId || "");
  const base = mode === "sandbox"
    ? "https://www.sandbox.paypal.com/sdk/js"
    : "https://www.paypal.com/sdk/js";
  return `${base}?client-id=${encodedClientId}&currency=USD&intent=capture&commit=true&components=buttons,funding-eligibility,card-fields`;
};

const loadPayPalV5Sdk = (mode, clientId, clientToken = "") => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("PayPal SDK can only be loaded in the browser."));
  }

  if (!clientId) {
    return Promise.reject(new Error("Missing PayPal client ID."));
  }

  const scriptSrc = getPayPalV5ScriptSrc(mode, clientId, clientToken);
  const existingScript = document.getElementById(PAYPAL_V5_SCRIPT_ID);
  const existingClientToken = existingScript?.getAttribute("data-client-token");

  if (
    window.paypal?.Buttons &&
    existingScript?.getAttribute("src") === scriptSrc &&
    existingClientToken === clientToken
  ) {
    return Promise.resolve(window.paypal);
  }

  if (
    existingScript &&
    (existingScript.getAttribute("src") !== scriptSrc || existingClientToken !== clientToken)
  ) {
    existingScript.remove();
    delete window.paypal;
    delete window.__paypalV5ScriptPromise;
  }

  if (window.__paypalV5ScriptPromise) {
    return window.__paypalV5ScriptPromise;
  }

  window.__paypalV5ScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = PAYPAL_V5_SCRIPT_ID;
    script.src = scriptSrc;
    script.setAttribute("data-client-token", clientToken);
    script.async = true;

    script.onload = () => {
      if (!window.paypal?.Buttons) {
        reject(new Error("PayPal SDK v5 failed to initialize."));
        return;
      }
      resolve(window.paypal);
    };

    script.onerror = () => {
      reject(new Error("Unable to load PayPal SDK script."));
    };

    document.head.appendChild(script);
  });

  return window.__paypalV5ScriptPromise;
};


// Plan Data
const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: 399,
    outOfPocket: 1000,
    description: "Perfect for individuals starting their health journey",
    features: [
      "Annual health checkup",
      "Emergency room coverage",
      "Telehealth consultations",
      "$1,000 out-of-pocket max",
      "24/7 nurse hotline",
      "Generic prescriptions",
      "Basic gym membership",
      "$50 wellness cash back"
    ],
    highlight: false
  },
  {
    id: "intermediate",
    name: "Intermediate",
    price: 599,
    outOfPocket: 1500,
    description: "Comprehensive coverage for growing families",
    badge: "MOST POPULAR",
    features: [
      "Everything in Starter",
      "Specialist visits included",
      "Mental health coverage",
      "$1,500 out-of-pocket max",
      "Premium gym membership",
      "Brand-name prescriptions",
      "$100 wellness cash back",
      "$25 monthly gift cards"
    ],
    highlight: true
  },
  {
    id: "advanced",
    name: "Advanced",
    price: 899,
    outOfPocket: 2000,
    description: "Premium protection with exclusive benefits",
    features: [
      "Everything in Intermediate",
      "Dental & vision included",
      "Life insurance coverage",
      "$2,000 out-of-pocket max",
      "Elite gym + spa access",
      "International coverage",
      "$200 wellness cash back",
      "$50 monthly gift cards"
    ],
    highlight: false
  }
];

// Header Component
const Header = ({ onGetStarted }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <div className="flex items-center gap-2" data-testid="logo">
            <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-700" />
            <span className="text-lg sm:text-xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>
              Vitasheild
            </span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {["Plans", "Benefits", "How It Works", "FAQ"].map(item => (
              <button 
                key={item}
                onClick={() => scrollTo(item.toLowerCase().replace(/ /g, "-"))}
                className="text-gray-600 hover:text-emerald-700 transition-colors font-medium text-sm"
              >
                {item}
              </button>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a href="tel:1-800-VITA" className="flex items-center gap-2 text-gray-600 hover:text-emerald-700 text-sm">
              <Phone className="w-4 h-4" />
              <span className="hidden lg:inline">1-800-VITA</span>
            </a>
            <button 
              onClick={() => scrollTo("plans")}
              data-testid="header-cta"
              className="bg-emerald-700 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-emerald-800 transition-all text-sm shadow-lg shadow-emerald-700/25"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 -mr-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="md:hidden py-4 border-t border-gray-100 bg-white">
            <nav className="flex flex-col gap-2">
              {["Plans", "Benefits", "How It Works", "FAQ"].map(item => (
                <button 
                  key={item}
                  onClick={() => scrollTo(item.toLowerCase().replace(/ /g, "-"))}
                  className="text-left py-3 px-2 text-gray-700 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg"
                >
                  {item}
                </button>
              ))}
              <button 
                onClick={() => scrollTo("plans")}
                className="mt-2 bg-emerald-700 text-white py-3 rounded-full font-semibold text-center"
              >
                Get Started
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="hero-section" className="pt-20 sm:pt-24 pb-12 sm:pb-20 bg-gradient-to-b from-emerald-50 to-white min-h-[90vh] flex items-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Content */}
          <div className="text-center lg:text-left order-2 lg:order-1">
            <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
              <Sparkles className="w-4 h-4" />
              6-Month Trial Available
            </span>
            
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-gray-900 mb-4 sm:mb-6 leading-tight"
              style={{ fontFamily: 'Merriweather, serif' }}
            >
              Your Health,<br />
              <span className="text-emerald-700 font-normal">Our Priority</span>
            </h1>
            
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Comprehensive health coverage with gym benefits, cash back rewards, and gift cards. Plans starting at just $399/year.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mb-6 sm:mb-8">
              <button 
                onClick={() => scrollTo("plans")}
                data-testid="hero-cta"
                className="bg-emerald-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold hover:bg-emerald-800 transition-all shadow-lg shadow-emerald-700/25 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                View Plans
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button 
                onClick={() => scrollTo("benefits")}
                data-testid="hero-learn-more"
                className="bg-white text-emerald-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold border-2 border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all text-sm sm:text-base"
              >
                Learn More
              </button>
            </div>

            <div className="flex flex-wrap gap-4 sm:gap-6 justify-center lg:justify-start text-xs sm:text-sm text-gray-600">
              {["No hidden fees", "Cancel anytime", "Instant coverage"].map((item, i) => (
                <span key={i} className="flex items-center gap-1.5 sm:gap-2">
                  <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Image */}
          <div className="relative order-1 lg:order-2">
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="/images/family-hero.png"
                alt="Happy healthy family"
                className="w-full h-[280px] sm:h-[350px] lg:h-[450px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent" />
            </div>
            
            {/* Floating Cards */}
            <div className="absolute -bottom-4 left-2 sm:left-4 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-700" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">98%</p>
                <p className="text-xs sm:text-sm text-gray-500">Claims Approved</p>
              </div>
            </div>

            <div className="absolute -top-2 right-2 sm:right-4 bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-700" />
              </div>
              <div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">500K+</p>
                <p className="text-xs sm:text-sm text-gray-500">Members</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { value: "500K+", label: "Members", icon: Users },
    { value: "98%", label: "Claims Approved", icon: CheckCircle2 },
    { value: "24/7", label: "Support", icon: Clock },
    { value: "15+", label: "Years Trusted", icon: Award }
  ];

  return (
    <section data-testid="stats-section" className="py-8 sm:py-12 bg-white border-y border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
          {stats.map((stat, i) => (
            <div key={i} className="text-center" data-testid={`stat-${i}`}>
              <stat.icon className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-700 mx-auto mb-2" />
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs sm:text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PayPalWalletCheckout = ({ paypalConfig, createOrder, onApprove, isBusy, isPrimary = false }) => {
  const [walletError, setWalletError] = useState("");
  const [isWalletReady, setIsWalletReady] = useState(false);
  const paypalButtonsRef = useRef(null);
  const walletButtonsContainerRef = useRef(null);
  const createOrderRef = useRef(createOrder);
  const onApproveRef = useRef(onApprove);

  useEffect(() => {
    createOrderRef.current = createOrder;
    onApproveRef.current = onApprove;
  }, [createOrder, onApprove]);

  useEffect(() => {
    if (!paypalConfig?.clientId) {
      setWalletError("PayPal wallet checkout is temporarily unavailable.");
      return undefined;
    }

    let isMounted = true;
    const walletButtonsContainer = walletButtonsContainerRef.current;
    setWalletError("");
    setIsWalletReady(false);

    const setupWalletButtons = async () => {
      try {
        await loadPayPalV5Sdk(paypalConfig.mode, paypalConfig.clientId, paypalConfig.clientToken);

        if (!isMounted || !walletButtonsContainer) {
          return;
        }

        if (!walletButtonsContainer.isConnected || !document.body.contains(walletButtonsContainer)) {
          return;
        }

        walletButtonsContainer.innerHTML = "";

        const buttons = window.paypal.Buttons({
          style: {
            layout: "vertical",
            shape: "pill",
            label: "paypal",
            height: 45,
          },
          createOrder: async () => {
            const created = await createOrderRef.current({ returnSessionOrder: true });
            return created?.orderId;
          },
          onApprove: async (approvalData) => {
            await onApproveRef.current({
              id: approvalData?.orderID || approvalData?.orderId,
              payer: {
                payer_id: approvalData?.payerID,
              },
            });
          },
          onCancel() {
            toast.message("PayPal checkout was canceled. You can try again.");
          },
          onError(error) {
            const message = getPayPalErrorMessage(error);
            setWalletError(message);
            toast.error(message);
          },
        });

        if (!buttons) {
          throw new Error("Unable to initialize PayPal wallet buttons.");
        }

        const isEligible = typeof buttons.isEligible === "function" ? buttons.isEligible() : true;
        if (!isEligible) {
          setWalletError("PayPal wallet checkout is unavailable for this browser right now.");
          return;
        }

        await buttons.render(walletButtonsContainer);

        // Container may be removed while PayPal is rendering during fast modal
        // transitions; in that case, bail out quietly.
        if (!isMounted || !walletButtonsContainer.isConnected) {
          return;
        }

        paypalButtonsRef.current = buttons;
        setIsWalletReady(true);
      } catch (error) {
        const message = String(error?.message || "");
        const isUnmountRace =
          message.includes("Detected container element removed from DOM") ||
          message.includes("removed from DOM");

        // This is a benign race during component unmount/remount.
        if (isUnmountRace && !isMounted) {
          return;
        }

        console.error("Failed to set up PayPal v5 wallet buttons:", error);
        if (isMounted) {
          setWalletError(getPayPalErrorMessage(error));
        }
      }
    };

    setupWalletButtons();

    return () => {
      isMounted = false;
      paypalButtonsRef.current = null;
      if (walletButtonsContainer) {
        walletButtonsContainer.innerHTML = "";
      }
    };
  }, [paypalConfig]);

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 bg-white p-4">
      <p className="mb-2 text-xs text-slate-600">
        {isPrimary ? "Continue with PayPal checkout" : "Or continue with PayPal wallet checkout"}
      </p>
      <p className="mb-2 text-[11px] text-slate-500">
        Pay with PayPal or a card on the PayPal-hosted page.
      </p>
      <div
        ref={walletButtonsContainerRef}
        className={`min-h-[45px] ${!isWalletReady ? "opacity-70" : ""}`}
      />
      {walletError && (
        <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {walletError}
        </div>
      )}
    </div>
  );
};

const PayPalCardCheckout = ({ paypalConfig, createOrder, onApprove, isBusy }) => {
  const [cardError, setCardError] = useState("");
  const [isCardReady, setIsCardReady] = useState(false);
  const [isCardIneligible, setIsCardIneligible] = useState(false);
  const [isSubmittingCard, setIsSubmittingCard] = useState(false);
  const cardFieldRef = useRef(null);
  const nameContainerRef = useRef(null);
  const numberContainerRef = useRef(null);
  const expiryContainerRef = useRef(null);
  const cvvContainerRef = useRef(null);
  const createOrderRef = useRef(createOrder);
  const onApproveRef = useRef(onApprove);

  useEffect(() => {
    createOrderRef.current = createOrder;
    onApproveRef.current = onApprove;
  }, [createOrder, onApprove]);

  useEffect(() => {
    if (!paypalConfig?.clientId) {
      setIsCardIneligible(true);
      return undefined;
    }

    let isMounted = true;
    setCardError("");
    setIsCardReady(false);
    setIsCardIneligible(false);

    const setupCardFields = async () => {
      try {
        await loadPayPalV5Sdk(paypalConfig.mode, paypalConfig.clientId, paypalConfig.clientToken);

        if (!isMounted || !window.paypal?.CardFields) {
          return;
        }

        const cardField = window.paypal.CardFields({
          createOrder: async () => {
            const created = await createOrderRef.current({ returnSessionOrder: true });
            return created?.orderId;
          },
          onApprove: async (approvalData) => {
            await onApproveRef.current({
              id: approvalData?.orderID || approvalData?.orderId,
              payer: { payer_id: approvalData?.payerID },
            });
          },
          onError(error) {
            if (isMounted) {
              setCardError(getPayPalErrorMessage(error));
            }
          },
        });

        const isEligible = typeof cardField.isEligible === "function" ? cardField.isEligible() : true;
        if (!isEligible) {
          if (isMounted) {
            setIsCardIneligible(true);
          }
          return;
        }

        if (!isMounted) {
          return;
        }

        cardField.NameField().render(nameContainerRef.current);
        cardField.NumberField().render(numberContainerRef.current);
        cardField.ExpiryField().render(expiryContainerRef.current);
        cardField.CVVField().render(cvvContainerRef.current);

        cardFieldRef.current = cardField;
        setIsCardReady(true);
      } catch (error) {
        console.error("Failed to set up PayPal card fields:", error);
        if (isMounted) {
          setCardError(getPayPalErrorMessage(error));
        }
      }
    };

    setupCardFields();

    return () => {
      isMounted = false;
      cardFieldRef.current = null;
    };
  }, [paypalConfig]);

  const handleSubmit = async () => {
    if (!cardFieldRef.current) return;
    setIsSubmittingCard(true);
    setCardError("");
    try {
      await cardFieldRef.current.submit();
    } catch (error) {
      setCardError(getPayPalErrorMessage(error));
    } finally {
      setIsSubmittingCard(false);
    }
  };

  if (isCardIneligible) {
    return null;
  }

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
      <p className="mb-3 text-xs text-slate-600">Or pay directly with a debit or credit card</p>
      <div className="space-y-3">
        <div ref={nameContainerRef} />
        <div ref={numberContainerRef} />
        <div className="grid grid-cols-2 gap-3">
          <div ref={expiryContainerRef} />
          <div ref={cvvContainerRef} />
        </div>
      </div>
      {cardError && (
        <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {cardError}
        </div>
      )}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!isCardReady || isBusy || isSubmittingCard}
        className="mt-4 w-full bg-emerald-700 text-white py-2.5 rounded-full font-semibold hover:bg-emerald-800 transition-all disabled:opacity-60 text-sm"
      >
        {isSubmittingCard ? "Processing..." : "Pay with Card"}
      </button>
    </div>
  );
};

// Checkout Modal
const CheckoutModal = ({ plan, isOpen, onClose }) => {
  const [step, setStep] = useState("info"); // info, payment, success
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [isPayPalConfigLoading, setIsPayPalConfigLoading] = useState(false);
  const [paypalConfigError, setPaypalConfigError] = useState("");
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [isCapturingPayment, setIsCapturingPayment] = useState(false);
  const hasAttemptedPayPalConfigRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !plan) return;
    setStep("info");
    setFormData(INITIAL_FORM_DATA);
    setPaypalConfig(null);
    setPaypalConfigError("");
    setIsPayPalConfigLoading(false);
    setIsCreatingOrder(false);
    setIsCapturingPayment(false);
    hasAttemptedPayPalConfigRef.current = false;
  }, [isOpen, plan]);

  useEffect(() => {
    if (
      !isOpen ||
      !plan ||
      step !== "payment" ||
      paypalConfig ||
      hasAttemptedPayPalConfigRef.current
    ) {
      return;
    }

    let isActive = true;
    hasAttemptedPayPalConfigRef.current = true;

    const loadPayPalConfig = async () => {
      setIsPayPalConfigLoading(true);
      setPaypalConfigError("");

      try {
        const response = await axios.get(`${API}/config/paypal`, {
          timeout: PAYPAL_CONFIG_TIMEOUT_MS,
        });
        const clientId = response.data?.client_id;

        if (!clientId) {
          throw new Error("Missing PayPal checkout credentials in API response.");
        }

        const clientTokenResponse = await axios.get(`${API}/config/paypal/client-token`, {
          timeout: PAYPAL_CONFIG_TIMEOUT_MS,
        });
        const clientToken = clientTokenResponse.data?.client_token;

        if (!clientToken) {
          throw new Error("Missing PayPal card checkout token in API response.");
        }

        if (isActive) {
          setPaypalConfig({
            clientId,
            clientToken,
            mode: response.data?.mode || "live",
          });
        }
      } catch (error) {
        console.error("Failed to load PayPal configuration:", error);
        if (isActive) {
          setPaypalConfigError(
            getApiErrorMessage(
              error,
              "Secure checkout is temporarily unavailable. Please try again."
            )
          );
        }
      } finally {
        if (isActive) {
          setIsPayPalConfigLoading(false);
        }
      }
    };

    loadPayPalConfig();

    return () => {
      isActive = false;
    };
  }, [isOpen, plan, step, paypalConfig]);

  const handleRetryPayPalConfig = () => {
    setPaypalConfig(null);
    setPaypalConfigError("");
    setIsPayPalConfigLoading(false);
    hasAttemptedPayPalConfigRef.current = false;
  };

  const createPayPalOrder = async (paymentMethod = "paypal_wallet", options = {}) => {
    setIsCreatingOrder(true);
    try {
      const response = await axios.post(`${API}/payments/create`, {
        plan_id: plan.id,
        plan_name: plan.name,
        amount: plan.price,
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        is_trial: formData.isTrial,
        payment_method: paymentMethod,
      }, {
        timeout: PAYMENT_REQUEST_TIMEOUT_MS,
      });

      const orderId = response.data?.order_id;
      if (!orderId) {
        throw new Error("Order ID was not returned by the server.");
      }

      if (options?.returnFull) {
        return {
          orderId,
          approveUrl: response.data?.approve_url || "",
        };
      }

      if (options?.returnSessionOrder) {
        return { orderId };
      }

      return orderId;
    } catch (error) {
      console.error("Payment order creation error:", error);
      throw new Error(getApiErrorMessage(error, "Unable to start secure checkout. Please try again."));
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    if (formData.firstName && formData.lastName && formData.email) {
      setStep("payment");
    }
  };

  const handlePaymentSuccess = async (details) => {
    setIsCapturingPayment(true);
    try {
      await axios.post(`${API}/payments/capture`, {
        order_id: details.id,
        payer_id: details.payer?.payer_id,
        plan_id: plan.id,
        plan_name: plan.name,
        amount: plan.price,
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        is_trial: formData.isTrial
      }, {
        timeout: PAYMENT_REQUEST_TIMEOUT_MS,
      });
      setStep("success");
      toast.success("Payment successful! Welcome to Vitasheild!");
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || "";

      // If the payment was already captured upstream,
      // the backend will return 502/503 trying to re-capture an already-COMPLETED order.
      // This is not a real failure — the payment succeeded. Treat it as success.
      const isAlreadyCaptured =
        details?._alreadyCaptured === true &&
        (status === 502 || status === 503) &&
        (detail.includes("COMPLETED") ||
          detail.includes("ORDER_ALREADY_CAPTURED") ||
          detail.includes("ORDER_CANNOT_BE_SAVED"));

      if (isAlreadyCaptured) {
        console.warn("Payment already captured by PayPal SDK — recording as success:", detail);
        setStep("success");
        toast.success("Payment successful! Welcome to Vitasheild!");
        return;
      }

      const isMerchantCardCapabilityBlock =
        detail.includes("Payee account is not setup to be able to process card payments") ||
        detail.includes("Card payments are not enabled on this merchant account yet");

      if (isMerchantCardCapabilityBlock) {
        toast.error("Card checkout is unavailable right now. Please use PayPal account checkout.");
        return;
      }

      const fullError = {
        status,
        data: error?.response?.data,
        message: error?.message,
        detail,
      };
      console.error("Payment capture error:", fullError);
      toast.error(getApiErrorMessage(error, "Payment processing failed. Please try again."));
    } finally {
      setIsCapturingPayment(false);
    }
  };

  if (!isOpen || !plan) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center overflow-y-auto bg-black/50 backdrop-blur-sm sm:p-4">
      <div className="bg-white w-full max-w-md min-h-[100dvh] sm:min-h-0 max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-3xl shadow-2xl">
        {/* Header */}
        <div className="bg-white p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between shadow-sm">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900" style={{ fontFamily: 'Merriweather, serif' }}>
              {step === "success" ? "Welcome!" : `${plan.name} Plan`}
            </h3>
            <p className="text-emerald-700 font-semibold">${plan.price}/year</p>
          </div>
          <button 
            onClick={onClose}
            data-testid="close-modal"
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {step === "info" && (
            <form onSubmit={handleInfoSubmit} data-testid="checkout-form">
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      data-testid="checkout-first-name"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      data-testid="checkout-last-name"
                      value={formData.lastName}
                      onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                      className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    data-testid="checkout-email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                    required
                  />
                </div>
              </div>

              {/* Trial Option */}
              <div className="bg-emerald-50 rounded-xl p-4 mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="trial-checkbox"
                    checked={formData.isTrial}
                    onChange={(e) => setFormData({...formData, isTrial: e.target.checked})}
                    className="mt-1 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <div>
                    <span className="font-semibold text-gray-900 text-sm">6-Month Trial Period</span>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Try the full plan for 6 months. If not satisfied, get a full refund - no questions asked!
                    </p>
                  </div>
                </label>
              </div>

              <button
                type="submit"
                data-testid="continue-to-payment"
                className="w-full bg-emerald-700 text-white py-3 rounded-full font-semibold hover:bg-emerald-800 transition-all shadow-lg shadow-emerald-700/25 text-sm"
              >
                Continue to Payment
              </button>
            </form>
          )}

          {step === "payment" && (
            <div data-testid="payment-step">
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Plan</span>
                  <span className="font-medium">{plan.name}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Duration</span>
                  <span className="font-medium">1 Year</span>
                </div>
                {formData.isTrial && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Trial Period</span>
                    <span className="font-medium text-emerald-600">6 Months</span>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-emerald-700">${plan.price}.00</span>
                </div>
              </div>

                {isPayPalConfigLoading && (
                  <div className="w-full rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-center text-sm text-emerald-800">
                    Loading secure payment options...
                  </div>
                )}

                {paypalConfigError && (
                  <div className="w-full rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 mb-3">
                    <p>{paypalConfigError}</p>
                    <button
                      type="button"
                      onClick={handleRetryPayPalConfig}
                      className="mt-3 rounded-full border border-rose-300 px-4 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                    >
                      Retry payment setup
                    </button>
                  </div>
                )}

                {(isCreatingOrder || isCapturingPayment) && (
                  <div className="w-full rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-center text-sm text-emerald-800 mb-3">
                    Processing secure payment...
                  </div>
                )}

                {paypalConfig && (
                  <div className="rounded-2xl border border-emerald-100 bg-gradient-to-b from-white to-emerald-50 p-4">
                    <PayPalWalletCheckout
                      paypalConfig={paypalConfig}
                      createOrder={(options) => createPayPalOrder("paypal_wallet", options)}
                      onApprove={handlePaymentSuccess}
                      isBusy={isCreatingOrder || isCapturingPayment}
                      isPrimary
                    />
                    <PayPalCardCheckout
                      paypalConfig={paypalConfig}
                      createOrder={(options) => createPayPalOrder("card", options)}
                      onApprove={handlePaymentSuccess}
                      isBusy={isCreatingOrder || isCapturingPayment}
                    />
                  </div>
                )}

              <button
                onClick={() => setStep("info")}
                className="w-full mt-4 text-gray-600 text-sm hover:text-gray-900"
              >
                ← Back to details
              </button>
            </div>
          )}

          {step === "success" && (
            <div data-testid="success-step" className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-700" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">You're All Set!</h4>
              <p className="text-gray-600 mb-6 text-sm">
                Welcome to Vitasheild! Your {plan.name} plan is now active.
                {formData.isTrial && " Your 6-month trial period has begun."}
              </p>
              <button
                onClick={onClose}
                data-testid="close-success"
                className="bg-emerald-700 text-white px-8 py-3 rounded-full font-semibold hover:bg-emerald-800 transition-all text-sm"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Plans Section
const PlansSection = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  return (
    <section id="plans" data-testid="plans-section" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-xs sm:text-sm uppercase tracking-widest font-semibold text-emerald-700/70 mb-3 block">
            Choose Your Plan
          </span>
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-gray-900 mb-4"
            style={{ fontFamily: 'Merriweather, serif' }}
          >
            Plans Designed for <span className="text-emerald-700">Your Life</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
            Annual subscriptions with a 6-month trial period. Flexible coverage with amazing benefits.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {PLANS.map((plan) => (
            <div 
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 transition-all duration-300 hover:-translate-y-1 ${
                plan.highlight 
                  ? "border-2 border-emerald-600 shadow-xl shadow-emerald-700/10" 
                  : "border border-gray-200 shadow-sm hover:shadow-lg"
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-700 text-white text-xs font-bold px-4 py-1 rounded-full">
                  {plan.badge}
                </span>
              )}
              
              <h3 
                className="text-xl sm:text-2xl font-medium text-gray-900 mb-1"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                {plan.name}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 mb-4">{plan.description}</p>
              
              <div className="mb-6">
                <span className="text-4xl sm:text-5xl font-bold text-gray-900">${plan.price}</span>
                <span className="text-gray-500">/year</span>
                <p className="text-xs sm:text-sm text-emerald-600 font-medium mt-1">
                  ${plan.outOfPocket} out-of-pocket max
                </p>
              </div>

              <ul className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 sm:gap-3 text-sm">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => handleSelectPlan(plan)}
                data-testid={`select-plan-${plan.id}`}
                className={`w-full py-3 sm:py-4 rounded-full font-semibold transition-all text-sm sm:text-base ${
                  plan.highlight
                    ? "bg-emerald-700 text-white hover:bg-emerald-800 shadow-lg shadow-emerald-700/25"
                    : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                }`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-xs sm:text-sm text-gray-500 mt-6 sm:mt-8">
          All plans include a 6-month trial with full money-back guarantee
        </p>
      </div>

      <CheckoutModal 
        plan={selectedPlan}
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
      />
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    { icon: Heart, title: "Health Insurance", description: "Comprehensive medical coverage for you and your family." },
    { icon: Dumbbell, title: "Gym Benefits", description: "Access to premium gyms and fitness centers nationwide." },
    { icon: CreditCard, title: "Cash Back Rewards", description: "Earn up to $200 in wellness cash back annually." },
    { icon: Gift, title: "Monthly Gift Cards", description: "Receive gift cards worth up to $50 monthly." },
    { icon: HeartHandshake, title: "Life Insurance", description: "Added life insurance coverage on Advanced plans." },
    { icon: Zap, title: "Fast Claims", description: "Claims processed within 48 hours guaranteed." }
  ];

  return (
    <section id="benefits" data-testid="benefits-section" className="py-16 sm:py-24 bg-emerald-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-xs sm:text-sm uppercase tracking-widest font-semibold text-emerald-700/70 mb-3 block">
            Why Vitasheild
          </span>
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-gray-900 mb-4"
            style={{ fontFamily: 'Merriweather, serif' }}
          >
            Benefits That <span className="text-emerald-700">Matter</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {benefits.map((benefit, i) => (
            <div 
              key={i}
              data-testid={`benefit-${i}`}
              className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 sm:w-14 sm:h-14 bg-emerald-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4">
                <benefit.icon className="w-5 h-5 sm:w-7 sm:h-7 text-emerald-700" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                {benefit.title}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    { num: "1", title: "Choose a Plan", desc: "Select the plan that fits your needs and budget." },
    { num: "2", title: "Quick Checkout", desc: "Complete payment securely with your debit or credit card." },
    { num: "3", title: "Start Trial", desc: "Begin your 6-month trial period immediately." },
    { num: "4", title: "Enjoy Coverage", desc: "Access all benefits and services right away." }
  ];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-xs sm:text-sm uppercase tracking-widest font-semibold text-emerald-700/70 mb-3 block">
            Simple Process
          </span>
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-gray-900 mb-4"
            style={{ fontFamily: 'Merriweather, serif' }}
          >
            Get Started in <span className="text-emerald-700">4 Steps</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
          {steps.map((step, i) => (
            <div key={i} data-testid={`step-${i}`} className="text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-700 text-white rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 text-xl sm:text-2xl font-bold">
                {step.num}
              </div>
              <h3 className="text-sm sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                {step.title}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    { quote: "Vitasheild's gym benefits alone are worth the price. Plus the cash back rewards are amazing!", name: "Sarah M.", role: "Fitness Enthusiast", avatar: "https://randomuser.me/api/portraits/women/44.jpg" },
    { quote: "The 6-month trial gave me confidence to try. Now I can't imagine life without Vitasheild.", name: "Michael C.", role: "Small Business Owner", avatar: "https://randomuser.me/api/portraits/men/32.jpg" },
    { quote: "Monthly gift cards are a lovely bonus. The customer service is exceptional too!", name: "Emily R.", role: "Teacher", avatar: "https://randomuser.me/api/portraits/women/68.jpg" }
  ];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-16 sm:py-24 bg-emerald-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-xs sm:text-sm uppercase tracking-widest font-semibold text-emerald-700/70 mb-3 block">
            Testimonials
          </span>
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-gray-900"
            style={{ fontFamily: 'Merriweather, serif' }}
          >
            Loved by <span className="text-emerald-700">Thousands</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {testimonials.map((t, i) => (
            <div key={i} data-testid={`testimonial-${i}`} className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-sm">
              <div className="flex gap-1 mb-3 sm:mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm sm:text-base text-gray-700 mb-4 italic">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full" />
                <div>
                  <p className="font-semibold text-gray-900 text-sm sm:text-base">{t.name}</p>
                  <p className="text-xs sm:text-sm text-gray-500">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [open, setOpen] = useState(null);
  const faqs = [
    { q: "How does the 6-month trial work?", a: "Pay the full annual fee upfront. If you're not satisfied within 6 months, we'll refund 100% - no questions asked. You keep all benefits during the trial." },
    { q: "When does my coverage start?", a: "Your coverage begins immediately after payment. You'll receive your digital insurance card within minutes." },
    { q: "What payment methods do you accept?", a: "Checkout is powered by PayPal, and you can pay with PayPal balance or major debit and credit cards." },
    { q: "Can I upgrade my plan later?", a: "Yes! You can upgrade anytime. We'll prorate the difference for the remaining period of your subscription." },
    { q: "How do the gift cards work?", a: "Intermediate and Advanced members receive monthly gift cards via email. Choose from popular retailers like Amazon, Target, and Starbucks." },
    { q: "Is gym membership included?", a: "Yes! All plans include gym benefits. Starter includes basic access, Intermediate includes premium gyms, and Advanced includes elite gyms with spa access." }
  ];

  return (
    <section id="faq" data-testid="faq-section" className="py-16 sm:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 sm:mb-16">
          <span className="text-xs sm:text-sm uppercase tracking-widest font-semibold text-emerald-700/70 mb-3 block">
            FAQ
          </span>
          <h2 
            className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight text-gray-900"
            style={{ fontFamily: 'Merriweather, serif' }}
          >
            Common <span className="text-emerald-700">Questions</span>
          </h2>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} data-testid={`faq-${i}`} className="border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? null : i)}
                data-testid={`faq-toggle-${i}`}
                className="w-full flex items-center justify-between p-4 sm:p-5 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-900 text-sm sm:text-base pr-4" style={{ fontFamily: 'Merriweather, serif' }}>
                  {faq.q}
                </span>
                <ChevronDown className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="px-4 sm:px-5 pb-4 sm:pb-5">
                  <p className="text-gray-600 text-sm sm:text-base">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer data-testid="footer" className="bg-gray-900 text-white py-12 sm:py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 mb-10 sm:mb-12">
        {/* Company Info */}
        <div className="sm:col-span-2 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-8 h-8 text-emerald-400" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>Vitasheild</span>
          </div>
          <p className="text-gray-400 text-sm mb-6 max-w-sm leading-relaxed">
            Vitasheild has been providing premium health insurance solutions for over 15 years, protecting families and individuals across the nation.
          </p>
          <div className="space-y-3 text-sm">
            <a href="tel:1-800-VITA-SHIELD" className="flex items-center gap-3 text-gray-300 hover:text-emerald-400 transition-colors">
              <Phone className="w-4 h-4 text-emerald-400" />
              1-800-VITA-SHIELD
            </a>
            <a href="mailto:support@vitasheild.com" className="flex items-center gap-3 text-gray-300 hover:text-emerald-400 transition-colors">
              <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              support@vitasheild.com
            </a>
            <div className="flex items-start gap-3 text-gray-400">
              <svg className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>123 Health Plaza, Suite 500,<br />New York, NY 10001</span>
            </div>
          </div>
        </div>

        {/* Plans */}
        <div>
          <h4 className="font-semibold mb-4 text-white">Plans</h4>
          <ul className="space-y-2.5 text-sm text-gray-400">
            <li><a href="#plans" className="hover:text-emerald-400 transition-colors">Essential Plan</a></li>
            <li><a href="#plans" className="hover:text-emerald-400 transition-colors">Family Plus</a></li>
            <li><a href="#plans" className="hover:text-emerald-400 transition-colors">Premium Elite</a></li>
            <li><a href="#plans" className="hover:text-emerald-400 transition-colors">Compare Plans</a></li>
          </ul>
        </div>

        {/* Resources */}
        <div>
          <h4 className="font-semibold mb-4 text-white">Resources</h4>
          <ul className="space-y-2.5 text-sm text-gray-400">
            <li><button className="hover:text-emerald-400 transition-colors">Find a Doctor</button></li>
            <li><button className="hover:text-emerald-400 transition-colors">Claims Center</button></li>
            <li><button className="hover:text-emerald-400 transition-colors">Member Portal</button></li>
            <li><button className="hover:text-emerald-400 transition-colors">Mobile App</button></li>
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="font-semibold mb-4 text-white">Company</h4>
          <ul className="space-y-2.5 text-sm text-gray-400">
            <li><button className="hover:text-emerald-400 transition-colors">About Us</button></li>
            <li><button className="hover:text-emerald-400 transition-colors">Careers</button></li>
            <li><button className="hover:text-emerald-400 transition-colors">Press</button></li>
            <li><button className="hover:text-emerald-400 transition-colors">Contact</button></li>
          </ul>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
        <p className="text-sm text-gray-500">© 2026 Vitasheild Insurance. All rights reserved.</p>
        <div className="flex items-center gap-6 text-sm text-gray-400">
          <button className="hover:text-emerald-400 transition-colors">Privacy Policy</button>
          <button className="hover:text-emerald-400 transition-colors">Terms of Service</button>
          <button className="hover:text-emerald-400 transition-colors">Accessibility</button>
        </div>
      </div>
    </div>
  </footer>
);

// Main App
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Header />
      <main>
        <HeroSection />
        <StatsSection />
        <PlansSection />
        <BenefitsSection />
        <HowItWorksSection />
        <TestimonialsSection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
